import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { Alert, AlertDescription, AlertTitle } from './ui/alert';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from './ui/dialog';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Checkbox } from './ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { ArrowLeft, Loader2, Printer, FileText, AlertCircle, X, Ban } from 'lucide-react';
import { creditDebitNoteApiService, CreditDebitNoteHeaderDto, CreditDebitNoteDetailDto, getCreditDebitNoteLineDescription } from '../services/creditDebitNoteApi';
import { branchApiService, Branch } from '../services/branchApi';
import { customerApiService, Customer } from '../services/customerApi';
import { invoiceApiService } from '../services/invoiceApi';
import { hsnApiService, HsnCode } from '../services/hsnApi';
import { taxRateHistoryApiService } from '../services/taxRateHistoryApi';
import { formatDate, formatDigitalSignedOnLine } from '../utils/dateUtils';
import { resolveDisplayTotalTax } from '../utils/taxUtils';
import { authApiService } from '../services/authApi';
import { useAuth } from '../contexts/AuthContext';
import { getInvoiceConfig, userHasEinvoiceCancelRole } from '../config/uiConfig';
import { toast } from 'sonner';
import { userApiService } from '../services/userApi';
import {
  CHUNK_CONTINUATION_ROWS,
  CHUNK_FIRST_PAGE_ROWS,
} from './InvoiceChunkedPrint';
import { CreditDebitNoteEinvoiceChunkPage } from './CreditDebitNoteEinvoiceChunkPage';

// Backend may store tax rate as percentage (18) or basis points (1800 = 18%). Normalize for calculation and display.
function toEffectiveRate(value: number): number {
  if (value == null || Number.isNaN(value)) return 0;
  return value > 100 ? value / 100 : value;
}
function toDisplayRate(value: number): number {
  if (value == null || Number.isNaN(value)) return 0;
  return value > 100 ? value / 100 : value;
}

function amountInWords(num: number): string {
  const ones = ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine', 'Ten',
    'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen', 'Seventeen', 'Eighteen', 'Nineteen'];
  const tens = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];

  const convertHundreds = (n: number): string => {
    let result = '';
    if (n >= 100) {
      result += ones[Math.floor(n / 100)] + ' Hundred ';
      n %= 100;
    }
    if (n >= 20) {
      result += tens[Math.floor(n / 10)] + ' ';
      n %= 10;
    }
    if (n > 0) result += ones[n] + ' ';
    return result.trim();
  };

  if (num === 0) return 'Zero';
  const whole = Math.floor(num);
  const paise = Math.round((num % 1) * 100);
  const numStr = whole.toString();
  let result = '';

  if (numStr.length > 7) {
    const crores = parseInt(numStr.slice(0, numStr.length - 7), 10);
    if (crores > 0) result += convertHundreds(crores) + ' Crore ';
  }
  if (numStr.length > 5) {
    const lakhs = parseInt(numStr.slice(Math.max(0, numStr.length - 7), numStr.length - 5), 10);
    if (lakhs > 0) result += convertHundreds(lakhs) + ' Lakh ';
  }
  if (numStr.length > 3) {
    const thousands = parseInt(numStr.slice(Math.max(0, numStr.length - 5), numStr.length - 3), 10);
    if (thousands > 0) result += convertHundreds(thousands) + ' Thousand ';
  }
  const hundreds = parseInt(numStr.slice(Math.max(0, numStr.length - 3)), 10);
  if (hundreds > 0) result += convertHundreds(hundreds);

  result = result.trim() + ' Rupees';
  if (paise > 0) result += ' and ' + convertHundreds(paise) + ' Paise';
  return result.trim();
}

interface HsnWiseRow {
  hsn: string;
  taxableValue: number;
  cgstRate: number;
  cgstAmount: number;
  sgstRate: number;
  sgstAmount: number;
  igstRate: number;
  igstAmount: number;
  totalTaxAmount: number;
}

/** Same paging as tax invoice e-print: first page rows + continuation pages. */
function chunkCreditNoteLineItems(
  details: CreditDebitNoteDetailDto[],
  firstPageRows: number,
  continuationRows: number
): CreditDebitNoteDetailDto[][] {
  if (details.length === 0) return [[]];
  if (details.length <= firstPageRows) return [details];
  const chunks: CreditDebitNoteDetailDto[][] = [details.slice(0, firstPageRows)];
  let rest = details.slice(firstPageRows);
  while (rest.length > 0) {
    chunks.push(rest.slice(0, continuationRows));
    rest = rest.slice(continuationRows);
  }
  return chunks;
}

function parseEinvoiceErrorPayload(res: unknown): { title: string; detail?: string } | null {
  if (res == null || typeof res !== 'object') return null;
  const o = res as Record<string, unknown>;
  if (o.success === true) return null;
  const summary = typeof o.message === 'string' ? o.message.trim() : '';
  let inner = '';
  const d = o.details;
  if (d != null && typeof d === 'object') {
    const m = (d as Record<string, unknown>).message;
    if (typeof m === 'string') inner = m.trim();
  }
  if (!inner && !summary) return { title: 'E-invoice generation failed.' };
  if (inner && summary && inner !== summary) {
    return { title: inner, detail: summary };
  }
  return { title: inner || summary || 'E-invoice generation failed.' };
}

const EINVOICE_CANCEL_REASONS = [
  { value: '1', label: 'Duplicate' },
  { value: '2', label: 'Data entry mistake' },
  { value: '3', label: 'Order Cancelled' },
  { value: '4', label: 'Others' },
] as const;

const EINVOICE_CANCEL_WINDOW_MS = 24 * 60 * 60 * 1000;

function getEinvoiceGeneratedAt(note: CreditDebitNoteHeaderDto | null): Date | null {
  if (!note) return null;
  const noteRec = note as unknown as Record<string, unknown>;
  const raw = note.ackDate ?? note.signedOn ?? noteRec.signed_on;
  if (!raw) return null;
  const parsed = new Date(String(raw));
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

function canCancelEinvoiceWithin24Hours(note: CreditDebitNoteHeaderDto | null, isEInvoiced: boolean): boolean {
  if (!note || !isEInvoiced) return false;
  if (!String(note.irn ?? '').trim()) return false;
  const generatedAt = getEinvoiceGeneratedAt(note);
  if (!generatedAt) return false;
  const elapsedMs = Date.now() - generatedAt.getTime();
  return elapsedMs >= 0 && elapsedMs < EINVOICE_CANCEL_WINDOW_MS;
}

export function CreditDebitNoteViewPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [note, setNote] = useState<CreditDebitNoteHeaderDto | null>(null);
  const [branchDetails, setBranchDetails] = useState<Branch | null>(null);
  const [shipToCustomer, setShipToCustomer] = useState<Customer | null>(null);
  const [billToCustomer, setBillToCustomer] = useState<Customer | null>(null);
  const [loading, setLoading] = useState(true);
  const [generatingEinvoice, setGeneratingEinvoice] = useState(false);
  const [einvoiceError, setEinvoiceError] = useState<{ title: string; detail?: string } | null>(null);
  const [isPasswordDialogOpen, setIsPasswordDialogOpen] = useState(false);
  const [einvoicePassword, setEinvoicePassword] = useState('');
  const [einvoicePasswordError, setEinvoicePasswordError] = useState('');
  const [einvoiceEwayBillRequired, setEinvoiceEwayBillRequired] = useState(true);
  const [validatingPassword, setValidatingPassword] = useState(false);
  const [printCopies, setPrintCopies] = useState(1);
  const [einvoiceSignerName, setEinvoiceSignerName] = useState('');
  const [isCancelEinvoiceDialogOpen, setIsCancelEinvoiceDialogOpen] = useState(false);
  const [cancelEinvoiceReason, setCancelEinvoiceReason] = useState('');
  const [cancellingEinvoice, setCancellingEinvoice] = useState(false);
  const [cancelEinvoiceError, setCancelEinvoiceError] = useState<{ title: string; detail?: string } | null>(null);
  const [isCancelPasswordDialogOpen, setIsCancelPasswordDialogOpen] = useState(false);
  const [cancelEinvoicePassword, setCancelEinvoicePassword] = useState('');
  const [cancelEinvoicePasswordError, setCancelEinvoicePasswordError] = useState('');
  const [validatingCancelPassword, setValidatingCancelPassword] = useState(false);
  const [userRoleNames, setUserRoleNames] = useState<string[]>([]);
  const [userRolesLoaded, setUserRolesLoaded] = useState(false);
  const originalDocumentTitleRef = useRef<string | null>(null);
  const [referenceInvoiceInfo, setReferenceInvoiceInfo] = useState<{ invoiceNo?: string; invoiceDate?: string } | null>(null);
  const [hsnDisplayRates, setHsnDisplayRates] = useState<
    Record<string, { cgstRate: number; sgstRate: number; igstRate: number }>
  >({});

  const items: CreditDebitNoteDetailDto[] = note?.details ?? [];

  const loadNote = useCallback(async () => {
    if (!id) return;
    try {
      setLoading(true);
      const h = await creditDebitNoteApiService.getById(id);
      setNote(h ?? null);
      if (!h) {
        setBranchDetails(null);
        setShipToCustomer(null);
        setBillToCustomer(null);
      } else {
        const branchPromise = h.branchId ? branchApiService.getBranchById(String(h.branchId)) : Promise.resolve(null);
        const shipToId = h.shipTo ?? h.billTo;
        const billToId = h.billTo;
        const [branchRes, shipRes, billRes] = await Promise.all([
          branchPromise,
          shipToId ? customerApiService.getCustomerById(String(shipToId)) : Promise.resolve({ success: false, data: null }),
          billToId ? customerApiService.getCustomerById(String(billToId)) : Promise.resolve({ success: false, data: null }),
        ]);
        setBranchDetails(branchRes ?? null);
        setShipToCustomer(shipRes?.success && shipRes?.data ? shipRes.data : null);
        setBillToCustomer(billRes?.success && billRes?.data ? billRes.data : null);
      }
    } catch {
      setNote(null);
      setBranchDetails(null);
      setShipToCustomer(null);
      setBillToCustomer(null);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    loadNote();
  }, [loadNote]);

  useEffect(() => {
    if (!user?.id) {
      setUserRoleNames([]);
      setUserRolesLoaded(true);
      return;
    }
    let cancelled = false;
    setUserRolesLoaded(false);
    void (async () => {
      try {
        const names = await userApiService.getUserRoleNamesByUserId(user.id, true);
        if (cancelled) return;
        setUserRoleNames(names);
      } catch {
        if (!cancelled) setUserRoleNames([]);
      } finally {
        if (!cancelled) setUserRolesLoaded(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [user?.id]);

  useEffect(() => {
    if (!note?.details?.length || !branchDetails || !billToCustomer) {
      setHsnDisplayRates({});
      return;
    }
    let cancelled = false;
    const loadRates = async () => {
      const uniqueHsns = Array.from(
        new Set(items.map((i) => (i.hsn ?? '').trim()).filter(Boolean))
      );
      if (uniqueHsns.length === 0) {
        if (!cancelled) setHsnDisplayRates({});
        return;
      }
      const hsnCodesList = await hsnApiService.getHsnCodes();
      const hsnCodes: HsnCode[] = Array.isArray(hsnCodesList) ? hsnCodesList : [];
      const sameState =
        (branchDetails.stateCode ?? '') === (billToCustomer.stateCode ?? '');
      const dateForApi = note.taxDate
        ? String(note.taxDate).slice(0, 10)
        : note.noteDate
          ? String(note.noteDate).slice(0, 10)
          : '';
      const map: Record<string, { cgstRate: number; sgstRate: number; igstRate: number }> = {};
      await Promise.all(
        uniqueHsns.map(async (code) => {
          const hsnRecord = hsnCodes.find((h) => (h.hsnCode ?? '') === code);
          if (!hsnRecord) return;
          try {
            const applicable = await taxRateHistoryApiService.getApplicableTaxRate(hsnRecord.id, dateForApi);
            if (sameState) {
              map[code] = {
                cgstRate: toDisplayRate(applicable.cgstRate ?? 0),
                sgstRate: toDisplayRate(applicable.sgstRate ?? 0),
                igstRate: 0,
              };
            } else {
              map[code] = {
                cgstRate: 0,
                sgstRate: 0,
                igstRate: toDisplayRate(applicable.igstRate ?? 0),
              };
            }
          } catch {
            if (sameState) {
              map[code] = {
                cgstRate: toDisplayRate(hsnRecord.currentCgstRate ?? 0),
                sgstRate: toDisplayRate(hsnRecord.currentSgstRate ?? 0),
                igstRate: 0,
              };
            } else {
              map[code] = {
                cgstRate: 0,
                sgstRate: 0,
                igstRate: toDisplayRate(hsnRecord.currentIgstRate ?? 0),
              };
            }
          }
        })
      );
      if (!cancelled) setHsnDisplayRates(map);
    };
    void loadRates();
    return () => {
      cancelled = true;
    };
  }, [note, branchDetails, billToCustomer, items]);

  // Load reference invoice info (number + date) when backend doesn't include date on the note payload
  useEffect(() => {
    const refId =
      note?.referenceInvoiceId ??
      (note as any)?.invoiceHeaderId ??
      (note as any)?.invoiceHeaderID ??
      (note as any)?.invoiceId ??
      (note as any)?.referenceInvoiceHeaderId ??
      (note as any)?.reference_invoice_header_id;
    // Always prefer invoice header API for number/date
    if (!refId) {
      const fallbackNo =
        note?.referenceInvoiceNo ?? (note as { invoiceNo?: string } | null)?.invoiceNo ?? undefined;
      const fallbackDate =
        (note as { invoiceDate?: string } | null)?.invoiceDate ??
        (note as { invoice_date?: string } | null)?.invoice_date ??
        (note as { referenceInvoiceDate?: string } | null)?.referenceInvoiceDate ??
        (note as { reference_invoice_date?: string } | null)?.reference_invoice_date ??
        undefined;
      const cleanNo = typeof fallbackNo === 'string' && fallbackNo.trim() ? fallbackNo.trim() : undefined;
      const cleanDate = typeof fallbackDate === 'string' && fallbackDate.trim() ? fallbackDate.trim() : undefined;
      setReferenceInvoiceInfo(cleanNo || cleanDate ? { invoiceNo: cleanNo, invoiceDate: cleanDate } : null);
      return;
    }

    setReferenceInvoiceInfo(null);
    let cancelled = false;
    (async () => {
      try {
        const inv = await invoiceApiService.getInvoiceHeaderById(String(refId));
        if (cancelled || !inv?.success || !inv.data) return;
        setReferenceInvoiceInfo({
          invoiceNo: (inv.data as any).invoiceNo ?? undefined,
          invoiceDate: (inv.data as any).invoiceDate ?? undefined,
        });
      } catch {
        // ignore
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [note]);

  useEffect(() => {
    if (!note) {
      setEinvoiceSignerName('');
      return;
    }
    const st = String(note.status ?? '')
      .toUpperCase()
      .replace(/-/g, '_');
    if (st !== 'E_INVOICED') {
      setEinvoiceSignerName('');
      return;
    }
    const fromApi = note.signedByName?.trim();
    if (fromApi) {
      setEinvoiceSignerName(fromApi);
      return;
    }
    const uid = note.signedBy ?? (note as { signed_by?: number }).signed_by;
    if (uid == null || Number.isNaN(Number(uid))) {
      setEinvoiceSignerName('');
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await userApiService.getUserById(String(uid));
        if (cancelled || !res.success || !res.data) return;
        const u = res.data;
        const display =
          [u.firstName, u.lastName].filter(Boolean).join(' ').trim() ||
          u.displayName ||
          u.fullName ||
          '';
        if (!cancelled) setEinvoiceSignerName(display);
      } catch {
        if (!cancelled) setEinvoiceSignerName('');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [note]);

  const statusNorm = String(note?.status ?? '')
    .toUpperCase()
    .replace(/-/g, '_');
  const isEInvoiced = statusNorm === 'E_INVOICED';
  const isEInvoicedCancelled = statusNorm === 'E_INVOICED_CANCELLED';
  const invoiceConfig = getInvoiceConfig();
  const enabledEinvoiceBranchCodes = invoiceConfig.einvoiceEnabledBranchCodes.map((code) =>
    String(code).toUpperCase()
  );
  const isEinvoiceEnabledForBranch =
    enabledEinvoiceBranchCodes.length === 0 ||
    enabledEinvoiceBranchCodes.includes(String(branchDetails?.branchCode ?? '').toUpperCase());
  const canCancelEinvoice =
    userRolesLoaded &&
    userHasEinvoiceCancelRole(userRoleNames) &&
    isEinvoiceEnabledForBranch &&
    canCancelEinvoiceWithin24Hours(note, isEInvoiced);

  const handlePrintWithCopies = async () => {
    const input = window.prompt('How many copies do you want to print?', String(printCopies || 1));
    if (input == null) return;
    const n = Math.max(1, Math.min(20, parseInt(input, 10) || 1));
    setPrintCopies(n);
    const noteNo =
      note?.noteNumber ??
      (note as { creditDebitNoteNo?: string } | null)?.creditDebitNoteNo ??
      (note?.id != null ? String(note.id) : '');
    if (noteNo) {
      if (originalDocumentTitleRef.current == null) originalDocumentTitleRef.current = document.title;
      document.title = noteNo;
    }
    setTimeout(() => window.print(), 50);
  };

  useEffect(() => {
    setPrintCopies(isEInvoiced ? 4 : 1);
  }, [isEInvoiced]);

  useEffect(() => {
    const prev = window.onafterprint;
    window.onafterprint = () => {
      setPrintCopies(isEInvoiced ? 4 : 1);
      if (originalDocumentTitleRef.current != null) {
        document.title = originalDocumentTitleRef.current;
        originalDocumentTitleRef.current = null;
      }
      if (typeof prev === 'function') prev.call(window, new Event('afterprint'));
    };
    return () => {
      window.onafterprint = prev ?? null;
    };
  }, [isEInvoiced]);

  const handleGenerateEinvoice = async () => {
    if (!id) return;
    setEinvoiceError(null);
    try {
      setGeneratingEinvoice(true);
      const res = await creditDebitNoteApiService.generateEInvoice(id, einvoiceEwayBillRequired);
      if (res.success) {
        setEinvoiceError(null);
        toast.success('E-invoice generated successfully');
        await loadNote();
      } else {
        const parsed = parseEinvoiceErrorPayload(res);
        if (parsed) setEinvoiceError(parsed);
        toast.error(parsed?.title ?? (res as { message?: string }).message ?? 'Failed to generate e-invoice');
      }
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : 'Failed to generate e-invoice';
      setEinvoiceError({ title: msg });
      toast.error(msg);
    } finally {
      setGeneratingEinvoice(false);
    }
  };

  const handleCancelEinvoice = async () => {
    if (!id || !note) return;
    const irn = String(note.irn ?? '').trim();
    if (!irn) {
      toast.error('IRN is missing on this note.');
      return;
    }
    if (!cancelEinvoiceReason) {
      toast.error('Please select a reason for cancellation.');
      return;
    }
    setCancelEinvoiceError(null);
    try {
      setCancellingEinvoice(true);
      const selectedReason = EINVOICE_CANCEL_REASONS.find((r) => r.value === cancelEinvoiceReason);
      const res = await creditDebitNoteApiService.cancelEInvoice(id, {
        irn,
        cnlrsn: cancelEinvoiceReason,
        cnlrem: selectedReason?.label ?? '',
      });
      if (res.success) {
        toast.success('E-invoice cancelled successfully');
        setIsCancelEinvoiceDialogOpen(false);
        setCancelEinvoiceReason('');
        await loadNote();
      } else {
        const parsed = parseEinvoiceErrorPayload(res);
        if (parsed) setCancelEinvoiceError(parsed);
        toast.error(parsed?.title ?? (res as { message?: string }).message ?? 'Failed to cancel e-invoice');
      }
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : 'Failed to cancel e-invoice';
      setCancelEinvoiceError({ title: msg });
      toast.error(msg);
    } finally {
      setCancellingEinvoice(false);
    }
  };

  const openCancelPasswordDialog = () => {
    if (!user?.username) {
      toast.error('Current user information is unavailable.');
      return;
    }
    if (!cancelEinvoiceReason) {
      toast.error('Please select a reason for cancellation.');
      return;
    }
    setCancelEinvoicePassword('');
    setCancelEinvoicePasswordError('');
    setIsCancelPasswordDialogOpen(true);
  };

  const handleCancelPasswordVerification = async () => {
    if (!user?.username) {
      toast.error('Current user information is unavailable.');
      return;
    }
    if (!cancelEinvoicePassword.trim()) {
      setCancelEinvoicePasswordError('Please enter your password.');
      return;
    }
    try {
      setCancelEinvoicePasswordError('');
      setValidatingCancelPassword(true);
      await authApiService.authenticateUser(user.username, cancelEinvoicePassword);
      setIsCancelPasswordDialogOpen(false);
      setCancelEinvoicePassword('');
      setCancelEinvoicePasswordError('');
      await handleCancelEinvoice();
    } catch {
      setCancelEinvoicePasswordError('Invalid password.');
    } finally {
      setValidatingCancelPassword(false);
    }
  };

  const openEinvoicePasswordDialog = () => {
    if (!user?.username) {
      toast.error('Current user information is unavailable.');
      return;
    }
    setEinvoicePassword('');
    setEinvoicePasswordError('');
    setEinvoiceEwayBillRequired(true);
    setIsPasswordDialogOpen(true);
  };

  const handlePasswordVerificationAndGenerate = async () => {
    if (!user?.username) {
      toast.error('Current user information is unavailable.');
      return;
    }
    if (!einvoicePassword.trim()) {
      setEinvoicePasswordError('Please enter your password.');
      return;
    }
    try {
      setEinvoicePasswordError('');
      setValidatingPassword(true);
      await authApiService.authenticateUser(user.username, einvoicePassword);
      setIsPasswordDialogOpen(false);
      setEinvoicePassword('');
      setEinvoicePasswordError('');
      await handleGenerateEinvoice();
    } catch {
      setEinvoicePasswordError('Invalid password.');
    } finally {
      setValidatingPassword(false);
    }
  };

  /** Same as invoice view: true when buyer state ≠ branch state → show IGST columns. */
  const isGst = useMemo(() => {
    if (!branchDetails || !billToCustomer) return true;
    return (branchDetails.stateCode ?? '') !== (billToCustomer.stateCode ?? '');
  }, [branchDetails, billToCustomer]);

  const { taxableAmount, totalCgst, totalSgst, totalIgst, totalTaxAmount, grandTotal, hsnWiseTaxDetails } = useMemo(() => {
    const lineAmt = (i: CreditDebitNoteDetailDto) => i.lineTotal ?? i.amount ?? (i.qty * i.rate);
    const taxable = items.reduce((s, i) => s + lineAmt(i), 0);

    const sumLineTaxFields = items.reduce(
      (s, i) => s + (i.cgst ?? 0) + (i.sgst ?? 0) + (i.igst ?? 0),
      0
    );
    const headerTax =
      note?.grandTotalTax != null && !Number.isNaN(Number(note.grandTotalTax))
        ? Number(note.grandTotalTax)
        : 0;
    let taxFieldsAreAmounts = false;
    if (headerTax > 0) {
      const diff = Math.abs(sumLineTaxFields - headerTax);
      if (diff <= 0.01 * Math.max(headerTax, 1)) {
        taxFieldsAreAmounts = true;
      }
    } else {
      taxFieldsAreAmounts = items.some(
        (i) => (i.cgst ?? 0) > 100 || (i.sgst ?? 0) > 100 || (i.igst ?? 0) > 100
      );
    }

    let totalCgstVal = 0;
    let totalSgstVal = 0;
    let totalIgstVal = 0;
    if (taxFieldsAreAmounts) {
      totalCgstVal = items.reduce((s, i) => s + (i.cgst ?? 0), 0);
      totalSgstVal = items.reduce((s, i) => s + (i.sgst ?? 0), 0);
      totalIgstVal = items.reduce((s, i) => s + (i.igst ?? 0), 0);
    } else {
      items.forEach((i) => {
        const amt = lineAmt(i);
        if (isGst) {
          totalIgstVal += (amt * toEffectiveRate(i.igst ?? 0)) / 100;
        } else {
          totalCgstVal += (amt * toEffectiveRate(i.cgst ?? 0)) / 100;
          totalSgstVal += (amt * toEffectiveRate(i.sgst ?? 0)) / 100;
        }
      });
    }

    const computedTotalTax = totalCgstVal + totalSgstVal + totalIgstVal;
    const totalTax = resolveDisplayTotalTax(computedTotalTax, headerTax);
    const grand =
      note?.grandTotal != null && !Number.isNaN(Number(note.grandTotal))
        ? Number(note.grandTotal)
        : taxable + totalTax;

    const byHsn: Record<
      string,
      { taxableValue: number; cgstAmount: number; sgstAmount: number; igstAmount: number }
    > = {};
    items.forEach((i) => {
      const hsn = i.hsn || 'N/A';
      const amt = lineAmt(i);
      if (!byHsn[hsn]) {
        byHsn[hsn] = { taxableValue: 0, cgstAmount: 0, sgstAmount: 0, igstAmount: 0 };
      }
      byHsn[hsn].taxableValue += amt;
      if (taxFieldsAreAmounts) {
        byHsn[hsn].cgstAmount += i.cgst ?? 0;
        byHsn[hsn].sgstAmount += i.sgst ?? 0;
        byHsn[hsn].igstAmount += i.igst ?? 0;
      } else if (isGst) {
        byHsn[hsn].igstAmount += (amt * toEffectiveRate(i.igst ?? 0)) / 100;
      } else {
        byHsn[hsn].cgstAmount += (amt * toEffectiveRate(i.cgst ?? 0)) / 100;
        byHsn[hsn].sgstAmount += (amt * toEffectiveRate(i.sgst ?? 0)) / 100;
      }
    });

    const hsnRows: HsnWiseRow[] = Object.entries(byHsn).map(([hsn, v]) => {
      const tv = v.taxableValue;
      let cgstRate = 0;
      let sgstRate = 0;
      let igstRate = 0;
      if (taxFieldsAreAmounts) {
        cgstRate = tv > 0 ? (v.cgstAmount / tv) * 100 : 0;
        sgstRate = tv > 0 ? (v.sgstAmount / tv) * 100 : 0;
        igstRate = tv > 0 ? (v.igstAmount / tv) * 100 : 0;
      } else {
        const fromMaster = hsnDisplayRates[hsn];
        if (fromMaster) {
          cgstRate = fromMaster.cgstRate;
          sgstRate = fromMaster.sgstRate;
          igstRate = fromMaster.igstRate;
        } else {
          const first = items.find((it) => (it.hsn || 'N/A') === hsn);
          cgstRate = toDisplayRate(first?.cgst ?? 0);
          sgstRate = toDisplayRate(first?.sgst ?? 0);
          igstRate = toDisplayRate(first?.igst ?? 0);
        }
      }
      const totalTaxRow = v.cgstAmount + v.sgstAmount + v.igstAmount;
      return {
        hsn,
        taxableValue: tv,
        cgstRate,
        cgstAmount: v.cgstAmount,
        sgstRate,
        sgstAmount: v.sgstAmount,
        igstRate,
        igstAmount: v.igstAmount,
        totalTaxAmount: totalTaxRow,
      };
    });

    return {
      taxableAmount: taxable,
      totalCgst: totalCgstVal,
      totalSgst: totalSgstVal,
      totalIgst: totalIgstVal,
      totalTaxAmount: totalTax,
      grandTotal: grand,
      hsnWiseTaxDetails: hsnRows,
    };
  }, [items, note, isGst, hsnDisplayRates]);

  const itemChunks = useMemo(
    () => chunkCreditNoteLineItems(items, CHUNK_FIRST_PAGE_ROWS, CHUNK_CONTINUATION_ROWS),
    [items]
  );

  const digitallySignedOnDisplay = useMemo(
    () => formatDigitalSignedOnLine(note as Record<string, unknown> | null),
    [note]
  );

  if (loading) {
    return (
      <div className="container mx-auto p-6 flex items-center justify-center min-h-[200px]">
        <Loader2 className="h-8 w-8 animate-spin text-primary-600" />
      </div>
    );
  }

  if (!note) {
    return (
      <div className="container mx-auto p-6">
        <p className="text-gray-600">Credit/Debit note not found.</p>
        <Button variant="outline" className="mt-4" onClick={() => navigate('/invoices/credit-debit-notes')}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to list
        </Button>
      </div>
    );
  }

  const totalQty = items.reduce((s, i) => s + i.qty, 0);
  const docTitle = (note.noteType === 'DEBIT_NOTE' || String(note.noteType).toUpperCase() === 'DEBIT') ? 'Debit Note' : 'Credit Note';

  const noteRec = note as Record<string, unknown>;
  const qrValueRaw =
    noteRec.signedQrcode ??
    noteRec.signedQRcode ??
    noteRec.signedQRCode ??
    noteRec.signedQrCode ??
    noteRec.qrCode ??
    noteRec.qrString ??
    '';
  const qrValue = String(qrValueRaw ?? '')
    .trim()
    .replace(/^"(.*)"$/, '$1');

  const branchDisplayName = branchDetails?.branchName ?? note.branchName ?? '';
  const copies = isEInvoiced ? Math.max(1, printCopies) : 1;

  return (
    <div className="container mx-auto p-6 space-y-4">
      {einvoiceError && (
        <Alert variant="destructive" className="print:hidden relative pr-10">
          <AlertCircle className="size-4" />
          <AlertTitle className="line-clamp-none whitespace-normal">E-invoice could not be generated</AlertTitle>
          <AlertDescription className="text-left col-start-2">
            <p className="font-medium text-destructive">{einvoiceError.title}</p>
            {einvoiceError.detail ? <p className="mt-2 text-sm opacity-90">{einvoiceError.detail}</p> : null}
          </AlertDescription>
          <button
            type="button"
            onClick={() => setEinvoiceError(null)}
            className="absolute right-3 top-3 rounded-sm opacity-70 transition-opacity hover:opacity-100 focus:outline-none"
            aria-label="Dismiss error"
          >
            <X className="h-4 w-4" />
          </button>
        </Alert>
      )}

      <div className="flex items-center justify-between mb-6 print:hidden">
        <Button variant="outline" onClick={() => navigate('/invoices/credit-debit-notes')}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back
        </Button>
        {isEInvoicedCancelled ? (
          <div className="flex items-center gap-2 rounded-md border border-red-200 bg-red-50 px-4 py-2 text-red-800">
            <Ban className="h-4 w-4 shrink-0" />
            <span className="font-medium">E-invoice is cancelled.</span>
          </div>
        ) : isEInvoiced ? (
          <div className="flex flex-wrap items-center gap-2">
            {canCancelEinvoice ? (
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setCancelEinvoiceError(null);
                  setIsCancelEinvoiceDialogOpen(true);
                }}
                disabled={cancellingEinvoice}
                className="border-red-200 text-red-700 hover:bg-red-50"
                title="Cancel e-invoice IRN (allowed within 24 hours of generation)"
              >
                {cancellingEinvoice ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <Ban className="h-4 w-4 mr-2" />
                )}
                Cancel e-invoice
              </Button>
            ) : null}
            <Button onClick={() => void handlePrintWithCopies()} className="bg-primary-600 hover:bg-primary-700 text-white">
              <Printer className="h-4 w-4 mr-2" />
              Print
            </Button>
          </div>
        ) : (
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => navigate(`/invoices/credit-debit-notes/${id}/edit`)}>
              Edit
            </Button>
            {isEinvoiceEnabledForBranch && (
              <Button
                onClick={openEinvoicePasswordDialog}
                disabled={generatingEinvoice || validatingPassword}
                className="bg-primary-600 hover:bg-primary-700 text-white"
              >
                <FileText className="h-4 w-4 mr-2" />
                {generatingEinvoice ? 'Generating...' : 'Generate e-invoice'}
              </Button>
            )}
          </div>
        )}
      </div>

      <Dialog
        open={isPasswordDialogOpen}
        onOpenChange={(open) => {
          if (validatingPassword) return;
          setIsPasswordDialogOpen(open);
          if (!open) {
            setEinvoicePassword('');
            setEinvoicePasswordError('');
            setEinvoiceEwayBillRequired(true);
          }
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirm Password</DialogTitle>
            <DialogDescription>
              Enter your password to generate the e-invoice for this note.
            </DialogDescription>
          </DialogHeader>
          <div className="flex items-start gap-2 rounded-md border border-gray-200 bg-gray-50 p-3">
            <Checkbox
              id="credit-debit-note-einvoice-eway"
              checked={einvoiceEwayBillRequired}
              onCheckedChange={(checked) => setEinvoiceEwayBillRequired(checked === true)}
              disabled={validatingPassword}
            />
            <div className="space-y-0.5">
              <Label htmlFor="credit-debit-note-einvoice-eway" className="cursor-pointer font-medium leading-snug">
                Generate e-way bill with this note
              </Label>
              <p className="text-xs text-gray-600">
                When enabled, the e-way bill is requested from GSP along with the e-invoice (if transport details allow).
              </p>
            </div>
          </div>
          <div className="space-y-2">
            <Label htmlFor="credit-debit-note-einvoice-password">Password</Label>
            <Input
              id="credit-debit-note-einvoice-password"
              type="password"
              value={einvoicePassword}
              onChange={(e) => {
                setEinvoicePassword(e.target.value);
                if (einvoicePasswordError) setEinvoicePasswordError('');
              }}
              autoFocus
              disabled={validatingPassword}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  void handlePasswordVerificationAndGenerate();
                }
              }}
            />
            {einvoicePasswordError ? (
              <p className="text-sm text-red-600">{einvoicePasswordError}</p>
            ) : null}
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setIsPasswordDialogOpen(false)}
              disabled={validatingPassword}
            >
              Cancel
            </Button>
            <Button
              onClick={() => void handlePasswordVerificationAndGenerate()}
              disabled={validatingPassword}
            >
              {validatingPassword ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Confirm
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={isCancelEinvoiceDialogOpen}
        onOpenChange={(open) => {
          if (cancellingEinvoice) return;
          setIsCancelEinvoiceDialogOpen(open);
          if (!open) {
            setCancelEinvoiceReason('');
            setCancelEinvoiceError(null);
          }
        }}
      >
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Cancel e-invoice</DialogTitle>
            <DialogDescription>
              Cancel the IRN via GST portal. This is allowed only within 24 hours of e-invoice generation.
            </DialogDescription>
          </DialogHeader>
          {cancelEinvoiceError ? (
            <Alert variant="destructive">
              <AlertCircle className="size-4" />
              <AlertTitle>{cancelEinvoiceError.title}</AlertTitle>
              {cancelEinvoiceError.detail ? (
                <AlertDescription>{cancelEinvoiceError.detail}</AlertDescription>
              ) : null}
            </Alert>
          ) : null}
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="cdn-cancel-einvoice-note-no">Note No</Label>
              <Input
                id="cdn-cancel-einvoice-note-no"
                value={String(
                  note?.noteNumber ??
                    (note as { creditDebitNoteNo?: string } | null)?.creditDebitNoteNo ??
                    ''
                )}
                readOnly
                className="bg-gray-50"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="cdn-cancel-einvoice-irn">IRN</Label>
              <Input
                id="cdn-cancel-einvoice-irn"
                value={String(note?.irn ?? '')}
                readOnly
                className="bg-gray-50 font-mono text-xs break-all"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="cdn-cancel-einvoice-reason">Reason for cancellation</Label>
              <Select
                value={cancelEinvoiceReason || undefined}
                onValueChange={setCancelEinvoiceReason}
                disabled={cancellingEinvoice}
              >
                <SelectTrigger id="cdn-cancel-einvoice-reason">
                  <SelectValue placeholder="Select reason" />
                </SelectTrigger>
                <SelectContent>
                  {EINVOICE_CANCEL_REASONS.map((reason) => (
                    <SelectItem key={reason.value} value={reason.value}>
                      {reason.value} — {reason.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter className="!flex-row justify-end gap-3 pt-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => setIsCancelEinvoiceDialogOpen(false)}
              disabled={cancellingEinvoice}
            >
              Close
            </Button>
            <Button
              type="button"
              onClick={openCancelPasswordDialog}
              disabled={cancellingEinvoice || validatingCancelPassword || !cancelEinvoiceReason}
              className="bg-red-600 text-white hover:bg-red-700"
            >
              {cancellingEinvoice ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Cancelling…
                </>
              ) : (
                'Cancel'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={isCancelPasswordDialogOpen}
        onOpenChange={(open) => {
          if (validatingCancelPassword || cancellingEinvoice) return;
          setIsCancelPasswordDialogOpen(open);
          if (!open) {
            setCancelEinvoicePassword('');
            setCancelEinvoicePasswordError('');
          }
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirm Password</DialogTitle>
            <DialogDescription>
              Enter your password to cancel the e-invoice.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label htmlFor="cdn-cancel-einvoice-password">Password</Label>
            <Input
              id="cdn-cancel-einvoice-password"
              type="password"
              value={cancelEinvoicePassword}
              onChange={(e) => {
                setCancelEinvoicePassword(e.target.value);
                if (cancelEinvoicePasswordError) setCancelEinvoicePasswordError('');
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  void handleCancelPasswordVerification();
                }
              }}
              placeholder="Enter your password"
              disabled={validatingCancelPassword || cancellingEinvoice}
              className={cancelEinvoicePasswordError ? 'border-red-500 focus-visible:ring-red-500' : undefined}
            />
            {cancelEinvoicePasswordError ? (
              <p className="text-sm text-red-600">{cancelEinvoicePasswordError}</p>
            ) : null}
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setIsCancelPasswordDialogOpen(false);
                setCancelEinvoicePassword('');
                setCancelEinvoicePasswordError('');
              }}
              disabled={validatingCancelPassword || cancellingEinvoice}
            >
              Close
            </Button>
            <Button
              type="button"
              onClick={() => void handleCancelPasswordVerification()}
              disabled={validatingCancelPassword || cancellingEinvoice}
              className="bg-red-600 text-white hover:bg-red-700"
            >
              {validatingCancelPassword ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Verifying…
                </>
              ) : (
                'Confirm'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <div className={isEInvoiced ? 'w-full max-w-none min-w-0 overflow-x-auto print:overflow-visible' : ''}>
        <div className={isEInvoiced ? 'invoice-chunked-print-root invoice-chunked-print invoice-a4-stage' : ''}>
        {Array.from({ length: copies }).map((_, copyIndex) => {
          const copyLabel =
            copyIndex === 0
              ? 'ORIGINAL FOR RECIPIENT'
              : copyIndex === 1
                ? 'DUPLICATE FOR TRANSPORTER'
                : copyIndex === 2
                  ? 'TRIPLICATE FOR SUPPLIER'
                  : 'EXTRA COPY';
          return (
            <React.Fragment key={copyIndex}>
              <div className={isEInvoiced && copyIndex > 0 ? 'hidden print:block' : ''}>
                {!isEInvoiced ? (
                <div className="border rounded-lg p-6 bg-white shadow-sm print:shadow-none print:border print:border-gray-300">
                  <div className="flex items-start justify-center gap-6 mb-3 flex-wrap">
                    <h2 className="text-center font-bold text-xl mb-4">{docTitle}</h2>
                  </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 print:grid-cols-2 gap-6 mb-6 min-w-0">
          <div className="space-y-4 border rounded p-4 min-w-0">
            <div className="border-b pb-3">
              <div className="flex gap-4">
                {branchDetails && (
                  <>
                    <div className="flex-shrink-0">
                      <img src="/customer-logo/sg-group-logo.jpg" alt="Company" className="h-14 w-20 object-contain" />
                    </div>
                    <div className="text-sm min-w-0">
                      <p className="font-semibold">{branchDetails.branchName}</p>
                      <p className="text-gray-600">{branchDetails.address}</p>
                      <p className="text-gray-600">GSTIN/UIN: {branchDetails.gstin || 'NA'}</p>
                      <p className="text-gray-600">State Name: {branchDetails.stateName || 'NA'}, Code: {branchDetails.stateCode || 'NA'}</p>
                    </div>
                  </>
                )}
              </div>
            </div>
            <div className="border-b pb-3">
              <p className="font-semibold mb-2">Consignee (Ship to):</p>
              {shipToCustomer ? (
                <div className="text-sm text-gray-700">
                  <p className="font-medium">{shipToCustomer.customerName}</p>
                  <p>{shipToCustomer.address}</p>
                  <p>Vendor Code: {shipToCustomer.vendorCode ?? 'NA'}</p>
                  <p>GSTIN/UIN: {shipToCustomer.gstNo ?? 'NA'}</p>
                  <p>State Name: {shipToCustomer.stateName ?? 'NA'}, Code: {shipToCustomer.stateCode ?? 'NA'}</p>
                </div>
              ) : (
                <p className="text-sm text-gray-500">—</p>
              )}
            </div>
            <div>
              <p className="font-semibold mb-2">Buyer (Bill to):</p>
              {billToCustomer ? (
                <div className="text-sm text-gray-700">
                  <p className="font-medium">{billToCustomer.customerName}</p>
                  <p>{billToCustomer.address}</p>
                  <p>Vendor Code: {billToCustomer.vendorCode ?? 'NA'}</p>
                  <p>GSTIN/UIN: {billToCustomer.gstNo ?? 'NA'}</p>
                  <p>State Name: {billToCustomer.stateName ?? 'NA'}, Code: {billToCustomer.stateCode ?? 'NA'}</p>
                </div>
              ) : (
                <p className="text-sm text-gray-500">—</p>
              )}
            </div>
          </div>

          <div className="border border-gray-300 rounded overflow-hidden">
            <table className="w-full border-collapse text-sm">
              <tbody>
                <tr>
                  <td className="border border-gray-300 px-3 py-2 font-semibold w-[1%] whitespace-nowrap">e-Way Bill No.</td>
                  <td className="border border-gray-300 px-3 py-2">{(note as { ewayBillNo?: string }).ewayBillNo ?? ''}</td>
                  <td className="border border-gray-300 px-3 py-2 font-semibold w-[1%] whitespace-nowrap">{docTitle} No.</td>
                  <td className="border border-gray-300 px-3 py-2" colSpan={3}>{note.noteNumber ?? (note as { creditDebitNoteNo?: string }).creditDebitNoteNo ?? `#${note.id}`}</td>
                </tr>
                <tr>
                  <td className="border border-gray-300 px-3 py-2 font-semibold w-[1%] whitespace-nowrap">Dated</td>
                  <td className="border border-gray-300 px-3 py-2" colSpan={5}>{note.noteDate ? formatDate(note.noteDate) : (note as { creditDate?: string }).creditDate ? formatDate((note as { creditDate: string }).creditDate) : ''}</td>
                </tr>
                <tr>
                  <td className="border border-gray-300 px-3 py-2 font-semibold">Original Invoice No. & Date.</td>
                  <td className="border border-gray-300 px-3 py-2" colSpan={5}>
                    {(() => {
                      // const invNo =
                      //   referenceInvoiceInfo?.invoiceNo ??
                      //   note.referenceInvoiceNo ??
                      //   (note as { invoiceNo?: string }).invoiceNo ??
                      //   '';
                      // const invDateRaw =
                      //   referenceInvoiceInfo?.invoiceDate ??
                      //   ((note as { invoiceDate?: string; invoice_date?: string; referenceInvoiceDate?: string; reference_invoice_date?: string })
                      //     .invoiceDate ??
                      //     (note as any).invoice_date ??
                      //     (note as any).referenceInvoiceDate ??
                      //     (note as any).reference_invoice_date ??
                      //     '');
                      // const invDate = invDateRaw ? formatDate(invDateRaw) : '';
                      // if (!invNo && !invDate) return '—';
                      // if (!invDate) return invNo || '—';
                      const invNo = referenceInvoiceInfo?.invoiceNo;
                      const invDate = referenceInvoiceInfo?.invoiceDate ? formatDate(referenceInvoiceInfo.invoiceDate) : '';
                      if (!invNo && !invDate) return '—';
                      if (invNo && invDate) return `${invNo} (${invDate})`;
                      return invNo || invDate || '—';
                    })()}
                  </td>
                </tr>
                <tr>
                  <td className="border border-gray-300 px-3 py-2 font-semibold">Other References</td>
                  <td className="border border-gray-300 px-3 py-2" colSpan={5}>{(note as { otherReference?: string }).otherReference ?? (note as { referenceNoAndDate?: string }).referenceNoAndDate ?? '—'}</td>
                </tr>
                <tr>
                  <td className="border border-gray-300 px-3 py-2 font-semibold">Buyer&apos;s Order No.</td>
                  <td className="border border-gray-300 px-3 py-2">{(note as { buyerOrderNo?: string }).buyerOrderNo ?? '—'}</td>
                  <td className="border border-gray-300 px-3 py-2 font-semibold">Dated</td>
                  <td className="border border-gray-300 px-3 py-2" colSpan={3}>{(note as { buyerOrderDate?: string }).buyerOrderDate ? formatDate((note as { buyerOrderDate: string }).buyerOrderDate) : '—'}</td>
                </tr>
                <tr>
                  <td className="border border-gray-300 px-3 py-2 font-semibold">Dispatch Doc No.</td>
                  <td className="border border-gray-300 px-3 py-2">{(note as { dispatchDocNo?: string }).dispatchDocNo ?? '—'}</td>
                  <td className="border border-gray-300 px-3 py-2 font-semibold">Dispatched through</td>
                  <td className="border border-gray-300 px-3 py-2" colSpan={3}>{(note as { dispatchedThrough?: string }).dispatchedThrough ?? '—'}</td>
                </tr>
                <tr>
                  <td className="border border-gray-300 px-3 py-2 font-semibold">Destination</td>
                  <td className="border border-gray-300 px-3 py-2" colSpan={5}>{(note as { destination?: string }).destination ?? '—'}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        {/* Item details table (draft / non–e-invoice single page) */}
        {items.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full border-collapse border border-gray-300 text-center text-sm">
              <thead>
                <tr className="bg-gray-100">
                  <th className="border border-gray-300 px-2 py-2">SI No.</th>
                  <th className="border border-gray-300 px-2 py-2 text-left">Description of Goods</th>
                  <th className="border border-gray-300 px-2 py-2">HSN/SAC</th>
                  <th className="border border-gray-300 px-2 py-2">Quantity</th>
                  <th className="border border-gray-300 px-2 py-2">Rate</th>
                  <th className="border border-gray-300 px-2 py-2">per</th>
                  <th className="border border-gray-300 px-2 py-2">Amount</th>
                </tr>
              </thead>
              <tbody>
                {items.map((row, idx) => (
                  <tr key={row.id ?? idx}>
                    <td className="border border-gray-300 px-2 py-1">{idx + 1}</td>
                    <td className="border border-gray-300 px-2 py-1 text-left">{getCreditDebitNoteLineDescription(row)}</td>
                    <td className="border border-gray-300 px-2 py-1">{row.hsn ?? '—'}</td>
                    <td className="border border-gray-300 px-2 py-1">{row.qty}</td>
                    <td className="border border-gray-300 px-2 py-1">{row.rate}</td>
                    <td className="border border-gray-300 px-2 py-1">{row.uom ?? 'Nos'}</td>
                    <td className="border border-gray-300 px-2 py-1">{(row.lineTotal ?? row.amount ?? row.qty * row.rate).toFixed(2)}</td>
                  </tr>
                ))}
                <tr>
                  <td colSpan={3} className="border border-gray-300 px-2 py-1 text-right font-semibold"></td>
                  <td className="border border-gray-300 px-2 py-1 font-semibold">{totalQty} {items[0]?.uom ?? 'Nos'}</td>
                  <td colSpan={2} className="border border-gray-300"></td>
                  <td className="border border-gray-300 text-right font-semibold">{taxableAmount.toFixed(2)}</td>
                </tr>
                {!isGst && (
                  <>
                    <tr>
                      <td colSpan={4} className="border border-gray-300 text-right font-semibold">CGST</td>
                      <td colSpan={2} className="border border-gray-300"></td>
                      <td className="border border-gray-300 text-right font-semibold">{totalCgst.toFixed(2)}</td>
                    </tr>
                    <tr>
                      <td colSpan={4} className="border border-gray-300 text-right font-semibold">SGST</td>
                      <td colSpan={2} className="border border-gray-300"></td>
                      <td className="border border-gray-300 text-right font-semibold">{totalSgst.toFixed(2)}</td>
                    </tr>
                  </>
                )}
                {isGst && (
                  <tr>
                    <td colSpan={4} className="border border-gray-300 text-right font-semibold">IGST</td>
                    <td colSpan={2} className="border border-gray-300"></td>
                    <td className="border border-gray-300 text-right font-semibold">{totalIgst.toFixed(2)}</td>
                  </tr>
                )}
                <tr className="bg-gray-100 font-semibold">
                  <td colSpan={4} className="border border-gray-300 text-right py-2">TOTAL</td>
                  <td colSpan={2} className="border border-gray-300"></td>
                  <td className="border border-gray-300 text-right">₹ {grandTotal.toFixed(2)}</td>
                </tr>
              </tbody>
            </table>
          </div>
        ) : (
          <p className="text-sm text-gray-500 py-4">No line items.</p>
        )}

        <div className="mt-4 border-t pt-4">
          <p className="text-sm">
            <strong>Amount Chargeable (in words):</strong> INR {amountInWords(grandTotal)} Only
          </p>
        </div>

        {hsnWiseTaxDetails.length > 0 && (
          <div className="mt-4 border rounded overflow-hidden">
            <table className="w-full border-collapse text-sm">
              <thead>
                <tr className="bg-gray-50 border-b">
                  <th className="border border-gray-300 p-2 text-left">HSN/SAC</th>
                  <th className="border border-gray-300 p-2 text-left">Taxable Value</th>
                  {!isGst ? (
                    <>
                      <th className="border border-gray-300 p-2">CGST Rate</th>
                      <th className="border border-gray-300 p-2">CGST Amount</th>
                      <th className="border border-gray-300 p-2">SGST Rate</th>
                      <th className="border border-gray-300 p-2">SGST Amount</th>
                    </>
                  ) : (
                    <>
                      <th className="border border-gray-300 p-2">IGST Rate</th>
                      <th className="border border-gray-300 p-2">IGST Amount</th>
                    </>
                  )}
                  <th className="border border-gray-300 p-2">Total Tax Amount</th>
                </tr>
              </thead>
              <tbody>
                {hsnWiseTaxDetails.map((row, idx) => (
                  <tr key={idx} className="border-b">
                    <td className="border border-gray-300 p-2">{row.hsn}</td>
                    <td className="border border-gray-300 p-2">{row.taxableValue.toFixed(2)}</td>
                    {!isGst ? (
                      <>
                        <td className="border border-gray-300 p-2 text-center">{row.cgstRate.toFixed(2)}%</td>
                        <td className="border border-gray-300 p-2 text-right">{row.cgstAmount.toFixed(2)}</td>
                        <td className="border border-gray-300 p-2 text-center">{row.sgstRate.toFixed(2)}%</td>
                        <td className="border border-gray-300 p-2 text-right">{row.sgstAmount.toFixed(2)}</td>
                      </>
                    ) : (
                      <>
                        <td className="border border-gray-300 p-2 text-center">{row.igstRate.toFixed(2)}%</td>
                        <td className="border border-gray-300 p-2 text-right">{row.igstAmount.toFixed(2)}</td>
                      </>
                    )}
                    <td className="border border-gray-300 p-2 text-right">{row.totalTaxAmount.toFixed(2)}</td>
                  </tr>
                ))}
                <tr className="bg-gray-50 font-semibold">
                  <td className="border border-gray-300 p-2">TOTAL</td>
                  <td className="border border-gray-300 p-2">{taxableAmount.toFixed(2)}</td>
                  {!isGst ? (
                    <>
                      <td className="border border-gray-300 p-2" />
                      <td className="border border-gray-300 p-2 text-right">{totalCgst.toFixed(2)}</td>
                      <td className="border border-gray-300 p-2" />
                      <td className="border border-gray-300 p-2 text-right">{totalSgst.toFixed(2)}</td>
                    </>
                  ) : (
                    <>
                      <td className="border border-gray-300 p-2" />
                      <td className="border border-gray-300 p-2 text-right">{totalIgst.toFixed(2)}</td>
                    </>
                  )}
                  <td className="border border-gray-300 p-2 text-right">{totalTaxAmount.toFixed(2)}</td>
                </tr>
              </tbody>
            </table>
          </div>
        )}

        <div className="mt-4 border rounded p-4">
          <p className="text-sm">
            <strong>Tax Amount (in words):</strong> INR {amountInWords(totalTaxAmount)} Only
          </p>
          <p className="font-semibold mt-2">Company&apos;s PAN: {branchDetails?.panNo ?? 'NA'}</p>
        </div>

        <div className="flex justify-between gap-4 border-t items-start mt-4 pt-4">
          <div className="min-w-0 flex-1">
            <p className="font-semibold">Declaration:</p>
            <p className="text-sm text-gray-700">
              We declare that this note shows the actual price of the goods described and that all particulars are true and
              correct.
            </p>
          </div>
          <div className="text-right shrink-0 min-w-[11rem]">
            <p className="text-sm font-medium">Authorised Signatory</p>
          </div>
        </div>

        <p className="text-center text-sm text-gray-500 mt-6">This is a Computer Generated Document.</p>
                </div>
              ) : (
                <>
                  {itemChunks.map((chunk, chunkIndex) => {
                    const isLastChunk = chunkIndex === itemChunks.length - 1;
                    const globalStart = itemChunks.slice(0, chunkIndex).reduce((s, c) => s + c.length, 0);
                    const breakAfter: React.CSSProperties['pageBreakAfter'] =
                      !isLastChunk || copyIndex < copies - 1 ? 'always' : 'auto';
                    const nextPageNum = copyIndex * itemChunks.length + chunkIndex + 2;
                    return (
                      <CreditDebitNoteEinvoiceChunkPage
                        key={`${copyIndex}-${chunkIndex}`}
                        copyLabel={copyLabel}
                        chunk={chunk}
                        globalStart={globalStart}
                        nextPageNum={nextPageNum}
                        breakAfter={breakAfter}
                        isLastChunk={isLastChunk}
                        docTitle={docTitle}
                        note={note}
                        noteRec={noteRec}
                        qrValue={qrValue}
                        branchDetails={branchDetails}
                        shipToCustomer={shipToCustomer}
                        billToCustomer={billToCustomer}
                        isGst={isGst}
                        taxableAmount={taxableAmount}
                        totalCgst={totalCgst}
                        totalSgst={totalSgst}
                        totalIgst={totalIgst}
                        totalQty={totalQty}
                        grandTotal={grandTotal}
                        totalTaxAmount={totalTaxAmount}
                        hsnWiseTaxDetails={hsnWiseTaxDetails}
                        einvoiceSignerName={einvoiceSignerName}
                        digitallySignedOn={digitallySignedOnDisplay}
                        branchDisplayName={branchDisplayName}
                        amountInWords={amountInWords}
                      />
                    );
                  })}
                </>
              )}
              </div>
            </React.Fragment>
          );
        })}
        </div>
      </div>
    </div>
  );
}
